import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Firestore
} from '@angular/fire/firestore';
import { User } from '../interfaces/product.interface';

export interface UserFilters {
  role?: 'user' | 'admin' | 'super_admin';
  searchTerm?: string;
  status?: 'active' | 'inactive';
}

export interface UserManagementResponse {
  success: boolean;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private readonly USERS_COLLECTION = 'users';

  constructor(private firestore: Firestore) {}

  /**
   * Get all users with pagination and filtering
   */
  getUsers(
    pageSize: number = 20, 
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
    filters?: UserFilters
  ): Observable<{
    users: User[];
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    hasMore: boolean;
    total: number;
  }> {
    return from(this.getUsersFromFirebase(pageSize, lastDoc, filters));
  }

  /**
   * Get users by role
   */
  getUsersByRole(role: 'user' | 'admin' | 'super_admin'): Observable<User[]> {
    const usersRef = collection(this.firestore, this.USERS_COLLECTION);
    const q = query(
      usersRef,
      where('role', '==', role),
      orderBy('registrationDate', 'desc')
    );

    return from(getDocs(q)).pipe(
      map(snapshot => {
        const users: User[] = [];
        snapshot.forEach(doc => {
          const userData = doc.data() as User;
          users.push({ ...userData, id: doc.id });
        });
        return users;
      }),
      catchError(error => {
        console.error('Error fetching users by role:', error);
        return of([]);
      })
    );
  }

  /**
   * Get user by ID
   */
  getUserById(userId: string): Observable<User | null> {
    const userRef = doc(this.firestore, this.USERS_COLLECTION, userId);
    
    return from(getDoc(userRef)).pipe(
      map(doc => {
        if (doc.exists()) {
          return { id: doc.id, ...doc.data() } as User;
        }
        return null;
      }),
      catchError(error => {
        console.error('Error fetching user:', error);
        return of(null);
      })
    );
  }

  /**
   * Create new user
   */
  createUser(userData: Omit<User, 'id' | 'registrationDate'>): Observable<UserManagementResponse> {
    const userRef = collection(this.firestore, this.USERS_COLLECTION);
    const newUser = {
      ...userData,
      registrationDate: new Date()
    };

    return from(addDoc(userRef, newUser)).pipe(
      map(docRef => ({
        success: true,
        message: 'User created successfully!',
        data: { id: docRef.id, ...newUser }
      })),
      catchError(error => {
        console.error('Error creating user:', error);
        return of({
          success: false,
          message: 'Failed to create user: ' + error.message
        });
      })
    );
  }

  /**
   * Update user
   */
  updateUser(userId: string, updates: Partial<User>): Observable<UserManagementResponse> {
    const userRef = doc(this.firestore, this.USERS_COLLECTION, userId);
    
    return from(updateDoc(userRef, updates)).pipe(
      map(() => ({
        success: true,
        message: 'User updated successfully!'
      })),
      catchError(error => {
        console.error('Error updating user:', error);
        return of({
          success: false,
          message: 'Failed to update user: ' + error.message
        });
      })
    );
  }

  /**
   * Delete user
   */
  deleteUser(userId: string): Observable<UserManagementResponse> {
    const userRef = doc(this.firestore, this.USERS_COLLECTION, userId);
    
    return from(deleteDoc(userRef)).pipe(
      map(() => ({
        success: true,
        message: 'User deleted successfully!'
      })),
      catchError(error => {
        console.error('Error deleting user:', error);
        return of({
          success: false,
          message: 'Failed to delete user: ' + error.message
        });
      })
    );
  }

  /**
   * Change user role
   */
  changeUserRole(userId: string, newRole: 'user' | 'admin' | 'super_admin'): Observable<UserManagementResponse> {
    return this.updateUser(userId, { role: newRole });
  }

  /**
   * Activate/Deactivate user
   */
  toggleUserStatus(userId: string, isActive: boolean): Observable<UserManagementResponse> {
    return this.updateUser(userId, { 
      status: isActive ? 'active' : 'inactive'
    });
  }

  /**
   * Get user statistics
   */
  getUserStatistics(): Observable<{
    totalUsers: number;
    totalAdmins: number;
    totalSuperAdmins: number;
    activeUsers: number;
    inactiveUsers: number;
  }> {
    return from(this.getUsersFromFirebase(1000)).pipe(
      map(result => {
        const users = result.users;
        return {
          totalUsers: users.length,
          totalAdmins: users.filter(u => u.role === 'admin').length,
          totalSuperAdmins: users.filter(u => u.role === 'super_admin').length,
          activeUsers: users.filter(u => (u as any).status !== 'inactive').length,
          inactiveUsers: users.filter(u => (u as any).status === 'inactive').length
        };
      }),
      catchError(error => {
        console.error('Error fetching user statistics:', error);
        return of({
          totalUsers: 0,
          totalAdmins: 0,
          totalSuperAdmins: 0,
          activeUsers: 0,
          inactiveUsers: 0
        });
      })
    );
  }

  /**
   * Search users
   */
  searchUsers(searchTerm: string): Observable<User[]> {
    const usersRef = collection(this.firestore, this.USERS_COLLECTION);
    
    return from(getDocs(usersRef)).pipe(
      map(snapshot => {
        const users: User[] = [];
        snapshot.forEach(doc => {
          const userData = doc.data() as User;
          if (
            userData.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userData.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            userData.mobile?.includes(searchTerm)
          ) {
            users.push({ ...userData, id: doc.id });
          }
        });
        return users;
      }),
      catchError(error => {
        console.error('Error searching users:', error);
        return of([]);
      })
    );
  }

  /**
   * Private method to fetch users from Firebase
   */
  private async getUsersFromFirebase(
    pageSize: number,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
    filters?: UserFilters
  ): Promise<{
    users: User[];
    lastDoc?: QueryDocumentSnapshot<DocumentData>;
    hasMore: boolean;
    total: number;
  }> {
    try {
      const usersRef = collection(this.firestore, this.USERS_COLLECTION);
      let q = query(usersRef, orderBy('registrationDate', 'desc'), limit(pageSize));

      // Apply filters
      if (filters?.role) {
        q = query(q, where('role', '==', filters.role));
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const users: User[] = [];
      
              snapshot.forEach(doc => {
          const userData = doc.data() as User;
          users.push({ ...userData, id: doc.id });
        });

      // Apply search filter if provided
      let filteredUsers = users;
      if (filters?.searchTerm) {
        filteredUsers = users.filter(user => 
          user.name?.toLowerCase().includes(filters.searchTerm!.toLowerCase()) ||
          user.email?.toLowerCase().includes(filters.searchTerm!.toLowerCase()) ||
          user.mobile?.includes(filters.searchTerm!)
        );
      }

      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      const hasMore = snapshot.docs.length === pageSize;

      return {
        users: filteredUsers,
        lastDoc: lastVisible,
        hasMore,
        total: filteredUsers.length
      };
    } catch (error) {
      console.error('Error fetching users from Firebase:', error);
      throw error;
    }
  }
}
